import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Container,
  makeStyles,
  InputAdornment,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Visibility, VisibilityOff, LockOutlined, CheckCircle } from '@material-ui/icons';
import { useTranslation } from '../hooks/useTranslation';

// URL base da API - usa variável de ambiente ou fallback para produção
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  paper: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 500,
    margin: '0 auto',
  },
  formField: {
    marginBottom: theme.spacing(3),
    width: '100%'
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  icon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  title: {
    marginBottom: theme.spacing(3),
    textAlign: 'center',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  passwordRequirement: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  termsDialog: {
    minWidth: 600,
    maxHeight: 400,
    overflow: 'auto',
  },
  backToLogin: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  }
}));

// Componente para o popup de termos de serviço
const TermsDialog = ({ open, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('terms.title')}</DialogTitle>
      <DialogContent className={classes.termsDialog}>
        <Typography variant="body2" paragraph>
          <strong>{t('terms.title')}</strong>
        </Typography>
        
        <Typography variant="body2" paragraph>
          {t('terms.agreement')}
        </Typography>
        
        <Typography variant="body2" paragraph>
          <strong>{t('terms.authorizedUse')}</strong><br/>
          {t('terms.authorizedUseDescription')}
        </Typography>
        
        <Typography variant="body2" paragraph>
          <strong>{t('terms.userResponsibility')}</strong><br/>
          {t('terms.userResponsibilityDescription')}
        </Typography>
        
        <Typography variant="body2" paragraph>
          <strong>{t('terms.privacyAndDataProtection')}</strong><br/>
          {t('terms.privacyAndDataProtectionDescription')}
        </Typography>
        
        <Typography variant="body2" paragraph>
          <strong>{t('terms.limitationsOfResponsibility')}</strong><br/>
          {t('terms.limitationsOfResponsibilityDescription')}
        </Typography>
        
        <Typography variant="body2" paragraph>
          <strong>{t('terms.changesToTerms')}</strong><br/>
          {t('terms.changesToTermsDescription')}
        </Typography>
        
        <Typography variant="body2" paragraph>
          <strong>{t('terms.acceptance')}</strong><br/>
          {t('terms.acceptanceDescription')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">{t('terms.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const AccountPage = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation('common');
  
  // Estados para controle da página
  const [action, setAction] = useState('');
  const [token, setToken] = useState('');
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  
  // Estados para o formulário
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  
  // Estados para controle de envio
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const verifyToken = useCallback(async (tokenToVerify, actionParam) => {
    try {
      console.log('=== VERIFICANDO TOKEN ===');
      console.log('API_BASE_URL:', API_BASE_URL);
      console.log('Token recebido:', tokenToVerify);
      
      if (!API_BASE_URL) {
        console.error('API_BASE_URL não está definido!');
        setTokenError(t('account.errors.apiConfigNotFound'));
        return;
      }
      
      if (!tokenToVerify) {
        console.error('Token está vazio ou undefined!');
        setTokenError(t('account.errors.tokenNotProvided'));
        return;
      }
      
      // Verificar se o token precisa ser decodificado
      let processedToken = tokenToVerify;
      try {
        const decodedToken = decodeURIComponent(tokenToVerify);
        if (tokenToVerify !== decodedToken) {
          console.log('Token decodificado:', decodedToken);
          processedToken = decodedToken;
        }
      } catch (decodeError) {
        console.log('Erro ao decodificar token (usando original):', decodeError);
      }
      
      // Usar FormData para enviar como application/x-www-form-urlencoded
      const formData = new URLSearchParams();
      formData.append('token', processedToken);
      
      console.log('Enviando FormData:', formData.toString());
      
      const fullUrl = `${API_BASE_URL}/users/verify_token`;
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });
      
      console.log('Resposta recebida - Status:', response.status);
      
      // Clonar a resposta para poder ler o corpo múltiplas vezes
      const responseClone = response.clone();
      const responseText = await responseClone.text();
      console.log('Corpo da resposta:', responseText);
      

      
      if (response.ok) {
        console.log('✅ Token válido!');
        setTokenValid(true);
      } else {
        console.log('❌ Token inválido - Status:', response.status);
        try {
          const errorData = await response.json();
          console.log('Erro detalhado:', errorData);
          setTokenError(errorData.error || t('account.errors.invalidTokenOrExpired'));
        } catch (jsonError) {
          console.log('Erro ao fazer parse do JSON da resposta:', jsonError);
          const textResponse = await response.text();
          console.log('Resposta como texto:', textResponse);
          setTokenError(`${t('account.errors.apiError')}: ${textResponse}`);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar token:', error);
      setTokenError(t('account.errors.connectionError'));
    } finally {
      setIsValidatingToken(false);
    }
  }, [t]);
  
  // Extrair parâmetros da URL
  useEffect(() => {
    console.log('=== EXTRAINDO PARÂMETROS DA URL ===');
    console.log('URL params:', location.search);
    
    const urlParams = new URLSearchParams(location.search);
    const actionParam = urlParams.get('action');
    const tokenParam = urlParams.get('token');
    
    console.log('Ação:', actionParam);
    console.log('Token:', tokenParam);
    
    if (!actionParam || !tokenParam) {
      console.log('Parâmetros inválidos na URL');
      setTokenError(t('account.errors.invalidUrlParams'));
      setIsValidatingToken(false);
      return;
    }
    
    if (actionParam !== 'ACTIVATE_ACCOUNT' && actionParam !== 'RESET_PASSWORD') {
      console.log('Ação inválida:', actionParam);
      setTokenError(t('account.errors.invalidAction'));
      setIsValidatingToken(false);
      return;
    }
    
    console.log('Parâmetros válidos - prosseguindo...');
    setAction(actionParam);
    setToken(tokenParam);
    
    // Verificar se o token é válido
    verifyToken(tokenParam, actionParam);
  }, [location.search, t, verifyToken]);

  const validatePassword = (password) => {
    const hasMinLength = password.length >= 8;
    const hasLetters = /[a-zA-Z]/.test(password);
    const hasNumbers = /\d/.test(password);
    
    return hasMinLength && hasLetters && hasNumbers;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!password || !confirmPassword || (!acceptTerms && action === 'ACTIVATE_ACCOUNT')) {
      setError(t('account.errors.allFieldsRequired'));
      return;
    }

    if (!validatePassword(password)) {
      setError(t('account.errors.passwordMinLength'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('account.errors.passwordMismatch'));
      return;
    }

    if (action === 'ACTIVATE_ACCOUNT' && !acceptTerms) {
      setError(t('account.errors.mustAcceptTerms'));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const endpoint = action === 'ACTIVATE_ACCOUNT' 
        ? '/users/active_account' 
        : '/users/new_password_by_token';
      
      // Usar FormData para enviar como application/x-www-form-urlencoded
      const formData = new URLSearchParams();
      formData.append('token', token);
      formData.append('password', password);
      
      console.log('=== ENVIANDO FORMULÁRIO ===');
      console.log('Endpoint:', endpoint);
      console.log('Dados:', formData.toString());
      
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });
      
      if (response.ok) {
        console.log('Formulário enviado com sucesso!');
        setSuccess(true);
      } else {
        console.log('Erro no envio do formulário - Status:', response.status);
        try {
          const errorData = await response.json();
          console.log('Erro detalhado:', errorData);
          setError(errorData.error || t('account.errors.processError'));
        } catch (jsonError) {
          const textResponse = await response.text();
          console.log('Erro como texto:', textResponse);
          setError(`${t('account.errors.apiError')}: ${textResponse}`);
        }
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      setError(t('account.errors.connectionError'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleBackToLogin = () => {
    history.push('/login');
  };
  
  const getTitle = () => {
    if (action === 'ACTIVATE_ACCOUNT') {
      return t('account.activateAccount');
    } else if (action === 'RESET_PASSWORD') {
      return t('account.resetPassword');
    }
    return t('account.account');
  };
  
  const getSubtitle = () => {
    if (action === 'ACTIVATE_ACCOUNT') {
      return t('account.activateSubtitle');
    } else if (action === 'RESET_PASSWORD') {
      return t('account.resetSubtitle');
    }
    return '';
  };
  
  const getSuccessMessage = () => {
    if (action === 'ACTIVATE_ACCOUNT') {
      return t('account.activateSuccess');
    } else if (action === 'RESET_PASSWORD') {
      return t('account.resetSuccess');
    }
    return t('account.operationSuccess');
  };
  
  // Tela de carregamento da validação do token
  if (isValidatingToken) {
    return (
      <Container component="main" maxWidth="md">
        <div className={classes.root}>
          <Paper elevation={3} className={classes.paper}>
            <div className={classes.loadingContainer}>
              <CircularProgress size={50} />
              <Typography variant="h6" style={{ marginTop: 16 }}>
                {t('account.validatingToken')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('account.validatingTokenDescription')}
              </Typography>
            </div>
          </Paper>
        </div>
      </Container>
    );
  }
  
  // Tela de erro do token
  if (!tokenValid) {
    return (
      <Container component="main" maxWidth="md">
        <div className={classes.root}>
          <Paper elevation={3} className={classes.paper}>
            <LockOutlined className={classes.icon} />
            <Typography component="h1" variant="h5" className={classes.title}>
              {t('account.invalidToken')}
            </Typography>
            
            <Alert severity="error" style={{ width: '100%', marginBottom: 16 }}>
              {tokenError}
            </Alert>
            
            <Typography variant="body2" color="textSecondary" align="center" style={{ marginBottom: 16 }}>
              {t('account.tokenExpiredMessage')}
            </Typography>
            
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleBackToLogin}
              className={classes.submit}
            >
              {t('account.backToLogin')}
            </Button>
          </Paper>
        </div>
      </Container>
    );
  }
  
  // Tela de sucesso
  if (success) {
    return (
      <Container component="main" maxWidth="md">
        <div className={classes.root}>
          <Paper elevation={3} className={classes.paper}>
            <CheckCircle className={classes.icon} />
            <Typography component="h1" variant="h5" className={classes.title}>
              {t('account.successTitle')}
            </Typography>
            
            <Alert severity="success" style={{ width: '100%', marginBottom: 16 }}>
              {getSuccessMessage()}
            </Alert>
            
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleBackToLogin}
              className={classes.submit}
            >
              {t('account.backToLogin')}
            </Button>
          </Paper>
        </div>
      </Container>
    );
  }
  
  // Tela principal do formulário
  return (
    <>
      <Container component="main" maxWidth="md">
        <div className={classes.root}>
          <Paper elevation={3} className={classes.paper}>
            <LockOutlined className={classes.icon} />
            <Typography component="h1" variant="h5" className={classes.title}>
              {getTitle()}
            </Typography>
            
            <Typography variant="body2" color="textSecondary" align="center" style={{ marginBottom: 16 }}>
              {getSubtitle()}
            </Typography>
            
            <Divider style={{ width: '100%', marginBottom: 20 }} />
            
            {error && (
              <Alert severity="error" style={{ width: '100%', marginBottom: 16 }}>
                {error}
              </Alert>
            )}
            
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                                  name="password"
                  label={t('login:newPassword')}
                  type={showPassword ? 'text' : 'password'}
                id="password"
                className={classes.formField}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
                              <Typography variant="body2" className={classes.passwordRequirement}>
                  {t('login:passwordRequirements')}
                </Typography>
              
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                                  name="confirmPassword"
                  label={t('login:confirmNewPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                className={classes.formField}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              {action === 'ACTIVATE_ACCOUNT' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('account.acceptTerms')}
                      <Link 
                        component="button" 
                        type="button"
                        onClick={() => setShowTerms(true)}
                                                  style={{ textDecoration: 'underline' }}
                        >
                          {t('login:termsOfService')}
                        </Link>
                    </Typography>
                  }
                  style={{ marginBottom: 16 }}
                />
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                className={classes.submit}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} />
                ) : (
                  action === 'ACTIVATE_ACCOUNT' ? t('account.activateAccount') : t('account.resetPassword')
                )}
              </Button>
            </form>
            
            <div className={classes.backToLogin}>
              <Link 
                component="button" 
                type="button"
                onClick={handleBackToLogin}
                style={{ textDecoration: 'underline' }}
              >
                {t('account.backToLogin')}
              </Link>
            </div>
          </Paper>
        </div>
      </Container>
      
      <TermsDialog 
        open={showTerms} 
        onClose={() => setShowTerms(false)} 
      />
    </>
  );
};

export default AccountPage; 